"use client";

/**
 * The hire panel on a product page (2026-09, hire businesses — Moving Ads):
 * the rates, the deposit asked, the minimum days, what comes with the item,
 * and "Request to hire" — dates, name, phone → a request the desk confirms
 * (no money is taken online). Shown only for products marked for hire.
 */
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { CalendarDays, CheckCircle2, Loader2, Package, ShieldCheck, Send, X } from "lucide-react";
import { toast } from "@/lib/toast";
import { formatUgx, hireDays, hireFeeFor, hireOf, hireRateLabel, isForHire } from "@/lib/config/hire";

const inputCls =
  "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500";

export function HirePanel({ product }: { product: any }) {
  const hire = hireOf(product);
  const brandId = product?.brandId ? String(product.brandId) : null;
  const settings = useQuery(api.brands.hireSettings.getHireSettings, brandId ? { brandId: brandId as Id<"brands"> } : "skip");
  const request = useMutation(api.orders.hireRequests.requestHire);

  const [open, setOpen] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState<{ requestNumber: string; days: number; quotedFee?: number } | null>(null);

  if (!hire || !isForHire(product)) return null;

  const days = start && end ? hireDays(new Date(start).getTime(), new Date(end).getTime()) : 0;
  const fee = days ? hireFeeFor(product, days, qty) : null;
  // Is it free for those dates? Asked as soon as both dates are picked, so
  // nobody fills the whole form only to be told the camera is already out.
  const availability = useQuery(
    api.orders.hireAvailability.getHireDateAvailability,
    brandId && days > 0
      ? {
          brandId: brandId as Id<"brands">,
          productId: product._id as Id<"products">,
          startDate: new Date(start).getTime(),
          endDate: new Date(end).getTime(),
          quantity: qty,
        }
      : "skip",
  );
  const taken = availability ? !availability.ok : false;
  const kit: { name: string; qty: number; dailyRate?: number }[] = Array.isArray(product.hireKit) ? product.hireKit : [];
  const canRequest = settings === undefined ? true : settings?.websiteRequests !== false;
  const perDay = (hire.pricingType || "per_day") === "per_day";

  const submit = async () => {
    if (!brandId) return;
    if (!start || !end) return toast.error("Pick the start and return dates");
    if (new Date(end).getTime() <= new Date(start).getTime()) return toast.error("The return date must be after the start date");
    if (!name.trim()) return toast.error("Tell us your name");
    if (phone.replace(/\D/g, "").length < 9) return toast.error("Enter a phone number we can reach you on");
    setSending(true);
    try {
      const result = await request({
        brandId: brandId as Id<"brands">,
        productId: product._id as Id<"products">,
        quantity: qty,
        startDate: new Date(start).getTime(),
        endDate: new Date(end).getTime(),
        guestName: name.trim(),
        guestPhone: phone.trim(),
        notes: notes.trim() || undefined,
      });
      setDone(result as any);
    } catch (err: any) {
      toast.error(typeof err?.data === "string" ? err.data : "Could not send the request — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">For hire</p>
          <p className="text-2xl font-bold text-gray-900 mt-0.5">{hireRateLabel(product, product?.currency || "UGX")}</p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
            {perDay && Number(hire.weeklyRate) > 0 && <span>{formatUgx(hire.weeklyRate)} / week</span>}
            {Number(hire.deposit) > 0 && (
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5 text-amber-700" /> {formatUgx(hire.deposit)} refundable security
              </span>
            )}
            {Number(hire.minDays) > 1 && <span>Minimum {hire.minDays} days</span>}
          </div>
        </div>
        {canRequest && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 transition-colors"
          >
            <CalendarDays className="h-4 w-4" /> Request to hire
          </button>
        )}
      </div>
      {kit.length > 0 && (
        <div className="text-xs text-gray-700">
          <span className="inline-flex items-center gap-1 font-semibold text-gray-900">
            <Package className="h-3.5 w-3.5" /> Comes with
          </span>{" "}
          {kit.map((k) => `${k.qty} × ${k.name}${Number(k.dailyRate) > 0 ? ` (+${formatUgx(k.dailyRate)} / day)` : ""}`).join(", ")}
        </div>
      )}
      <p className="text-[11px] text-gray-500">
        Bring an ID on the day. The shop confirms your request on WhatsApp; you pay when you collect.
      </p>

      {open && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={() => !sending && setOpen(false)}>
          <div
            className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-white p-5 shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Request to hire</h3>
                <p className="text-xs text-gray-500">{product.name}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {done ? (
              <div className="text-center py-6 space-y-3">
                <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
                <p className="text-base font-semibold text-gray-900">Request sent</p>
                <p className="text-sm text-gray-600">
                  Your reference is <span className="font-mono font-semibold">{done.requestNumber}</span>. The shop will confirm on
                  WhatsApp.
                </p>
                {done.quotedFee ? (
                  <p className="text-sm text-gray-700">
                    {done.days} day{done.days === 1 ? "" : "s"} · {formatUgx(done.quotedFee)} hire fee, paid when you collect.
                  </p>
                ) : null}
                <button type="button" onClick={() => setOpen(false)} className="mt-2 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white">
                  Done
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">From</label>
                    <input type="date" value={start} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setStart(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Return by</label>
                    <input type="date" value={end} min={start || new Date().toISOString().slice(0, 10)} onChange={(e) => setEnd(e.target.value)} className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-[96px_1fr] gap-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">How many</label>
                    <input type="number" min={1} max={50} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1)))} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Your name</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">WhatsApp number</label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+256 7xx xxx xxx" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Anything we should know (optional)</label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="The shoot, the venue, extra lenses…" className={`${inputCls} resize-none`} />
                </div>
                {days > 0 && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-gray-800 flex items-center justify-between">
                    <span>
                      {days} day{days === 1 ? "" : "s"}
                      {qty > 1 ? ` · ${qty} units` : ""}
                    </span>
                    <span className="font-bold">{fee !== null ? formatUgx(fee) : "—"}</span>
                  </div>
                )}
                {taken && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
                    <p className="flex items-start gap-2">
                      <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                      <span>{availability?.message}</span>
                    </p>
                    {availability?.nextFreeFrom ? (
                      <button
                        type="button"
                        onClick={() => {
                          const from = new Date(availability.nextFreeFrom as number);
                          const to = new Date((availability.nextFreeFrom as number) + Math.max(0, new Date(end).getTime() - new Date(start).getTime()));
                          setStart(from.toISOString().slice(0, 10));
                          setEnd(to.toISOString().slice(0, 10));
                        }}
                        className="mt-2 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                      >
                        Use those dates
                      </button>
                    ) : null}
                  </div>
                )}
                <button
                  type="button"
                  onClick={submit}
                  disabled={sending || taken}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send request
                </button>
                <p className="text-[11px] text-gray-500 text-center">No payment now — the shop confirms first.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HirePanel;
